import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Box } from "@mui/material";
import {
  AutoFixHigh,
  Sync,
  AutoGraph,
  DoDisturb
} from '@mui/icons-material';
import { authTokenState, cognitoIdState } from 'features/auth/state/authAtoms';
import { displayStudyLoadingState, displayStudyState, selectedStudyState } from 'features/optimization/state/studyAtoms';
import { apiRenderKeyState } from 'shared/state/renderKeyAtoms';
import { yamlConfigState } from 'features/optimization/state/yamlConfigAtoms';
import { allTiresState, allWheelhousesState } from 'features/file-browser/state/fileAtoms';
import { CustomButton } from "shared/ui/CustomButton";
import { submitStudyStart } from 'features/optimization/api/submitStudyStart';
import { isSendable } from 'features/optimization/utils/isSendable';
import { isOptimizationCancelable } from 'features/optimization/utils/isCancelable';
import { fetchStudyStop } from 'features/optimization/api/fetchStudyStop';
import { fetchStudyResult } from 'features/optimization/api/fetchStudyResult';

export const OptimizationButtons: React.FC = () => {
  const setDisplayStudyState = useSetRecoilState(displayStudyState);
  const setDisplayStudyLoading = useSetRecoilState(displayStudyLoadingState);
  const setRenderKey = useSetRecoilState(apiRenderKeyState);

  const allTires = useRecoilValue(allTiresState);
  const allWheelhouses = useRecoilValue(allWheelhousesState);
  const authToken = useRecoilValue(authTokenState);
  const cognitoId = useRecoilValue(cognitoIdState);
  const selectedStudy = useRecoilValue(selectedStudyState);
  const yamlConfig = useRecoilValue(yamlConfigState);

  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [resultLoading, setResultLoading] = useState<boolean>(false);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(true);

  useEffect(() => {
    const res = isOptimizationCancelable(selectedStudy);
    setIsCancelDisabled(res);
  }, [selectedStudy]);

  const startClick = async () => {
    if (isSendable(yamlConfig, allTires, allWheelhouses)) {
      setApiLoading(true);
      await submitStudyStart(
        yamlConfig,
        authToken,
        cognitoId,
      );
      setRenderKey((p) => p + 1);
      setApiLoading(false);
    }
  };

  const cancelClick = async () => {
    if (selectedStudy) {
      setCancelLoading(true);
      await fetchStudyStop(authToken, selectedStudy.name);
      setRenderKey((p: number) => p + 1);
      setCancelLoading(false);
    }
  };

  const resultClick = async () => {
    if (selectedStudy) {
      setResultLoading(true);
      setDisplayStudyLoading(true);
      try {
        const res = await fetchStudyResult(authToken, selectedStudy.name);
        setDisplayStudyState(res);
      } finally {
        setResultLoading(false);
        setDisplayStudyLoading(false);
      }
    }
  };

  return (
    <Box component="div">

      <Box component="div" sx={{ display: 'flex', gap: 0.5 }}>
        <Box component="div" sx={{ width: '100%' }}>
          <CustomButton
            size='small'
            disabled={apiLoading}
            onClick={startClick}
            loading={apiLoading}
            loadingPosition='start'
            startIcon={<AutoFixHigh />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Start
          </CustomButton>
        </Box >
        <Box component="div" sx={{ width: '100%' }}>
          <CustomButton
            size='small'
            onClick={() => { setRenderKey((p) => p + 1) }}
            startIcon={<Sync />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Sync
          </CustomButton>
        </Box >
      </Box>

      <Box component="div" sx={{ display: 'flex', gap: 0.5, paddingTop: 0.5 }}>
        <Box component="div" sx={{ width: '100%' }}>
          <CustomButton
            size='small'
            disabled={isCancelDisabled}
            onClick={cancelClick}
            loading={cancelLoading}
            loadingPosition='start'
            startIcon={<DoDisturb />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Cancel
          </CustomButton>
        </Box >
        <Box component="div" sx={{ width: '100%' }}>
          <CustomButton
            size='small'
            disabled={!selectedStudy}
            onClick={resultClick}
            loading={resultLoading}
            loadingPosition='start'
            startIcon={<AutoGraph />}
            variant='contained'
            sx={{ fontSize: '10px', width: '100%' }}
          >
            Show Result
          </CustomButton>
        </Box >
      </Box>

    </Box>
  );
};
